import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import { useAuth } from '@/hooks/useAuth';
import MyLayout from '../../components/MyLayout';
import { fetchMyOrderById, type MyOrder } from '@/lib/myAccount';
import { courierTrackingUrl } from '@/lib/courierTracking';
import { supabase } from '@/lib/supabaseClient';
import { useToast } from '@/contexts/ToastContext';
import InvoiceView from './components/InvoiceView';

const statusColors: Record<string, string> = {
  pending: 'bg-[#F0EFED] text-[#8A8A8A]',
  paid: 'bg-[#7B1113]/10 text-[#7B1113]',
  shipped: 'bg-[#1E3A8A]/10 text-[#1E3A8A]',
  delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-100 text-gray-500',
};

interface TimelineStep {
  label: string;
  date: string;
  completed: boolean;
}

function buildTimeline(order: MyOrder, t: TFunction): TimelineStep[] {
  const steps: TimelineStep[] = [
    { label: t('order.timeline.placed', 'Order Placed'), date: order.placedAt, completed: true },
    { label: t('order.timeline.shipped', 'Shipped'), date: order.shipment?.dispatchedAt ?? '', completed: !!order.shipment?.dispatchedAt },
    { label: t('order.timeline.delivered', 'Delivered'), date: order.shipment?.deliveredAt ?? '', completed: !!order.shipment?.deliveredAt },
  ];
  if (order.rawStatus === 'cancelled' || order.rawStatus === 'returned') {
    steps.push({ label: t('order.timeline.cancelled', 'Cancelled'), date: '', completed: true });
  }
  return steps;
}

export default function MyOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { currentUser } = useAuth();
  const [order, setOrder] = useState<MyOrder | null | undefined>(undefined);

  useEffect(() => {
    if (!currentUser || !id) return;
    const load = () => fetchMyOrderById(currentUser.id, id).then(setOrder);
    load();

    // Live status — a courier dispatch/delivery update (or any other order
    // change) re-fetches this order automatically instead of leaving the
    // customer looking at a stale page until they manually reload.
    const channel = supabase
      .channel(`my-order-${id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'core_orders', filter: `id=eq.${id}` }, load)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'core_shipments', filter: `order_id=eq.${id}` }, load)
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentUser, id]);

  if (order === undefined) {
    return (
      <MyLayout title={t('my.orderDetail', 'Order Detail')}>
        <div className="p-10 text-center text-sm text-[#8A8A8A]">{t('common.loading')}</div>
      </MyLayout>
    );
  }

  if (!order) {
    return (
      <MyLayout title={t('my.orderDetail', 'Order Detail')}>
        <div className="bg-white rounded-2xl p-10 border border-[#E8E8E6] text-center">
          <p className="text-sm text-[#8A8A8A]">{t('order.notFound', 'Order not found')}</p>
          <button
            onClick={() => navigate(prefix + '/my/orders')}
            className="mt-4 px-6 py-2 bg-[#7B1113] text-white text-xs tracking-wider uppercase rounded-full cursor-pointer"
          >
            {t('common.back')}
          </button>
        </div>
      </MyLayout>
    );
  }

  const timeline = buildTimeline(order, t);

  return (
    <MyLayout title={`${t('my.orderDetail', 'Order Detail')} — ${order.orderNo}`}>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-6 print:hidden">
        {/* Order Header */}
        <div className="bg-white rounded-2xl p-6 border border-[#E8E8E6]">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="text-lg font-serif font-bold text-[#2A2A2A]">{order.orderNo}</h3>
              <p className="text-xs text-[#8A8A8A] mt-1">{order.placedAt.slice(0, 10)}</p>
            </div>
            <span className={`text-[10px] uppercase tracking-wider px-3 py-1 rounded-full ${statusColors[order.status]}`}>
              {order.status}
            </span>
          </div>

          {/* Timeline */}
          <div className="relative pl-4 border-l border-[#E8E8E6] flex flex-col gap-5 mt-6">
            {timeline.map((step, idx) => (
              <div key={idx} className="relative pl-6">
                <div
                  className={`absolute -left-[5px] top-1 w-2.5 h-2.5 rounded-full ${step.completed ? 'bg-[#7B1113]' : 'bg-[#E8E8E6]'}`}
                ></div>
                <p className="text-sm font-medium text-[#2A2A2A]">{step.label}</p>
                <p className="text-xs text-[#8A8A8A]">{step.date ? new Date(step.date).toLocaleString() : t('my.pending', 'Pending')}</p>
              </div>
            ))}
          </div>

          {order.shipment?.trackingNo && (
            <div className="mt-6 p-4 rounded-xl bg-[#F0EFED] flex flex-col sm:flex-row items-start sm:items-center gap-2">
              <span className="text-xs text-[#8A8A8A]">
                {order.shipment.courier ? `${order.shipment.courier} ${t('my.tracking', 'Tracking')}:` : `${t('my.tracking', 'Tracking')}:`}
              </span>
              {(() => {
                const url = courierTrackingUrl(order.shipment.courier, order.shipment.trackingNo);
                return url ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-[#7B1113] underline hover:no-underline"
                  >
                    {order.shipment.trackingNo}
                  </a>
                ) : (
                  <span className="text-sm font-medium text-[#2A2A2A]">{order.shipment.trackingNo}</span>
                );
              })()}
            </div>
          )}
        </div>

        {/* Items */}
        <div className="bg-white rounded-2xl p-6 border border-[#E8E8E6]">
          <h4 className="text-sm tracking-wider uppercase font-medium text-[#2A2A2A] mb-4">
            {t('order.orderDetails', 'Order Items')}
          </h4>
          {order.items.map((item) => (
            <div key={item.sku} className="flex items-center justify-between py-3 border-b border-[#E8E8E6] last:border-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#F0EFED] flex items-center justify-center text-xs font-bold text-[#7B1113]">
                  {item.qty}x
                </div>
                <span className="text-sm text-[#2A2A2A]">{item.name}</span>
              </div>
              <span className="text-sm font-medium text-[#2A2A2A]">৳{item.lineTotalBdt.toLocaleString()}</span>
            </div>
          ))}
          <div className="flex items-center justify-between pt-4 border-t border-[#E8E8E6]">
            <span className="text-sm font-medium text-[#2A2A2A]">{t('cart.total', 'Total')}</span>
            <span className="text-lg font-serif font-bold text-[#2A2A2A]">৳{order.totalBdt.toLocaleString()}</span>
          </div>
        </div>
        </div>

        {/* Invoice (only renders once one exists) */}
        <InvoiceView
          orderId={order.id}
          orderStatus={order.rawStatus}
          paymentMethod={order.paymentMethod}
          codAmountBdt={order.shipment?.codAmountBdt ?? null}
          deliveredAt={order.shipment?.deliveredAt ?? null}
        />

        <div className="flex flex-col gap-6 print:hidden">
        {/* Shipping Info */}
        <div className="bg-white rounded-2xl p-6 border border-[#E8E8E6]">
          <h4 className="text-sm tracking-wider uppercase font-medium text-[#2A2A2A] mb-4">
            {t('checkout.shipping', 'Shipping Address')}
          </h4>
          <div className="flex flex-col gap-1 text-sm text-[#2A2A2A]">
            <p>{order.customerName}</p>
            <p>{order.customerPhone}</p>
            <p className="text-[#8A8A8A]">{order.customerAddress}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={() => showToast(t('cart.added'))}
            className="w-full sm:w-auto px-6 py-3 bg-[#7B1113] text-white text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors cursor-pointer text-center h-12"
          >
            {t('my.reorder', 'Reorder')}
          </button>
          <Link
            to={prefix + '/my/orders'}
            className="w-full sm:w-auto px-6 py-3 border border-[#E8E8E6] text-[#2A2A2A] text-xs tracking-[0.15em] uppercase rounded-full hover:border-[#7B1113] hover:text-[#7B1113] transition-colors text-center h-12 flex items-center justify-center"
          >
            {t('common.back')}
          </Link>
        </div>
        </div>
      </div>
    </MyLayout>
  );
}
